import { ReturnStatus } from 'types/return';

export interface ShopifyReturnSyncPresentationInput {
  returnStatus?: ReturnStatus | string | null;
  syncStatus?: string | null;
  shopifyReturnId?: string | null;
}

export interface ShopifyReturnSyncPresentation {
  status: string;
  statusLabel: string;
  nativeReturnFallback: string;
  canRetry: boolean;
}

const POST_APPROVAL_RETURN_STATUSES = new Set<string>([
  ReturnStatus.APPROVED,
  ReturnStatus.AWAITING_DROPOFF,
  ReturnStatus.LABEL_READY,
  ReturnStatus.IN_TRANSIT,
  ReturnStatus.RECEIVED,
  ReturnStatus.RESOLVED,
  ReturnStatus.EXPIRED,
]);

const STATUS_LABELS: Record<string, string> = {
  SYNCED: 'Synced',
  CREATED: 'Created',
  PENDING: 'Syncing',
  RETRYING: 'Retrying',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
};

const displayStatusLabel = (status: string) =>
  STATUS_LABELS[status] ||
  status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const getShopifyReturnSyncPresentation = ({
  returnStatus,
  syncStatus,
  shopifyReturnId,
}: ShopifyReturnSyncPresentationInput): ShopifyReturnSyncPresentation => {
  const normalizedReturnStatus = String(returnStatus ?? '')
    .trim()
    .toUpperCase();
  const normalizedSyncStatus = String(syncStatus ?? '')
    .trim()
    .toUpperCase();

  if (normalizedSyncStatus) {
    return {
      status: normalizedSyncStatus,
      statusLabel: displayStatusLabel(normalizedSyncStatus),
      nativeReturnFallback: 'Not created yet',
      canRetry:
        normalizedSyncStatus === 'PENDING' ||
        ['FAILED', 'SKIPPED'].includes(normalizedSyncStatus),
    };
  }

  if (shopifyReturnId) {
    return {
      status: 'SYNCED',
      statusLabel: 'Synced',
      nativeReturnFallback: 'Not created yet',
      canRetry: false,
    };
  }

  if (normalizedReturnStatus === ReturnStatus.DENIED) {
    return {
      status: 'NOT_REQUIRED',
      statusLabel: 'Not required',
      nativeReturnFallback: 'Not required',
      canRetry: false,
    };
  }

  if (POST_APPROVAL_RETURN_STATUSES.has(normalizedReturnStatus)) {
    return {
      status: 'NEEDS_ATTENTION',
      statusLabel: 'Needs attention',
      nativeReturnFallback: 'Not synced',
      canRetry: true,
    };
  }

  return {
    status: 'WAITING_FOR_APPROVAL',
    statusLabel: 'Waiting for approval',
    nativeReturnFallback: 'Not started',
    canRetry: false,
  };
};
